import React, { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import BigNumber from 'bignumber.js'
import { useIndexPrice } from '../../../hooks/trade/indexprice'
import { useTradeSocket } from '../../../hooks/trade/tradeSocket'

interface MarketRowProps {
	item: any
	info24H: any
	isEven: boolean
	getSymbolColor: (code: string) => string
}

const Row = styled.div<{ $isEven: boolean }>`
	display: grid;
	grid-template-columns: 140px 90px 110px;
	padding: 8px 12px;
	background: ${props => props.$isEven ? '#fafafa' : '#fff'};
	border-bottom: 1px solid #f5f5f5;
	transition: background 0.15s ease;

	&:hover {
		background: #f0f7ff;
	}

	&:last-child {
		border-bottom: none;
	}
`

const SymbolCell = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`

const SymbolIcon = styled.div<{ $color: string }>`
	width: 28px;
	height: 28px;
	border-radius: 5px;
	background: ${props => props.$color};
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 9px;
	font-weight: 700;
	color: #fff;
`

const SymbolInfo = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1px;
`

const SymbolCode = styled.span`
	font-size: 12px;
	font-weight: 600;
	color: #1a1a2e;
`

const SymbolName = styled.span`
	font-size: 10px;
	color: #8c8c8c;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 90px;
`

const PriceCell = styled.div<{ $isPositive: boolean | null }>`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	font-size: 12px;
	font-weight: 600;
	color: ${props =>
		props.$isPositive === null ? '#1a1a2e' :
		props.$isPositive ? '#52c41a' : '#ff4d4f'
	};
	font-variant-numeric: tabular-nums;
`

const ChangeCell = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 4px;
`

const ChangeValue = styled.span<{ $isPositive: boolean | null }>`
	font-size: 11px;
	font-weight: 500;
	color: ${props =>
		props.$isPositive === null ? '#8c8c8c' :
		props.$isPositive ? '#52c41a' : '#ff4d4f'
	};
	font-variant-numeric: tabular-nums;
`

const ChangePercent = styled.span<{ $isPositive: boolean | null }>`
	font-size: 11px;
	font-weight: 600;
	color: ${props =>
		props.$isPositive === null ? '#8c8c8c' :
		props.$isPositive ? '#52c41a' : '#ff4d4f'
	};
	font-variant-numeric: tabular-nums;
`

const MarketRow: React.FC<MarketRowProps> = ({ item, info24H, isEven, getSymbolColor }) => {
	// 실시간 가격 데이터 - useIndexPrice 먼저 호출하여 SWR 캐시 초기화
	const { data: indexPrice } = useIndexPrice(item?.id)

	// WebSocket 연결
	const { joinGroup } = useTradeSocket()

	// WebSocket 그룹 조인 - useIndexPrice 초기화 후에 실행
	useEffect(() => {
		if (item?.id && item?.pairs) {
			const timer = setTimeout(() => {
				joinGroup(item.pairs, item.id)
			}, 100)
			return () => clearTimeout(timer)
		}
	}, [item?.id, item?.pairs])

	// 현재가: WebSocket 데이터 우선, 없으면 24H 데이터, 없으면 item.price
	const currentPrice = useMemo(() => {
		if (indexPrice?.[0]?.price) return indexPrice[0].price
		if (info24H?.close) return info24H.close
		return item?.price || 0
	}, [indexPrice, info24H, item?.price])

	// 등락률 계산 - HTS/WebFront와 동일한 방식으로 직접 계산
	// 전일종가(prev) 기준으로 현재가 대비 등락률
	const prevPrice = info24H?.prev || 0
	const change = useMemo(() => {
		if (!currentPrice || !prevPrice) return 0
		return BigNumber(currentPrice).minus(prevPrice).toNumber()
	}, [currentPrice, prevPrice])

	const rate = useMemo(() => {
		if (!currentPrice || !prevPrice || currentPrice === 0) return 0
		// HTS 공식: (현재가 - 전일종가) / 현재가 * 100
		return BigNumber(currentPrice)
			.minus(prevPrice)
			.multipliedBy(100)
			.dividedBy(currentPrice)
			.toNumber()
	}, [currentPrice, prevPrice])

	const isPositive = rate === 0 ? null : rate > 0

	// 가격 포맷팅
	const formatPrice = (price: number, dp: number = 2) => {
		if (!price || price === 0) return '-'
		return BigNumber(price).toFormat(dp)
	}

	// 변동률 포맷팅
	const formatPercent = (rate: number) => {
		if (rate === undefined || rate === null) return '0.00%'
		const sign = rate > 0 ? '+' : ''
		return `${sign}${BigNumber(rate).toFormat(2)}%`
	}

	// 변동금액 포맷팅
	const formatChange = (change: number) => {
		if (change === undefined || change === null) return '0.00'
		const sign = change > 0 ? '+' : ''
		return `${sign}${BigNumber(change).toFormat(2)}`
	}

	return (
		<Row $isEven={isEven}>
			<SymbolCell>
				<SymbolIcon $color={getSymbolColor(item.code)}>
					{item.code?.slice(0, 2)}
				</SymbolIcon>
				<SymbolInfo>
					<SymbolCode>{item.pairs}</SymbolCode>
					<SymbolName>{item.codename || item.name}</SymbolName>
				</SymbolInfo>
			</SymbolCell>
			<PriceCell $isPositive={isPositive}>
				{formatPrice(currentPrice, item.dp || 2)}
			</PriceCell>
			<ChangeCell>
				<ChangeValue $isPositive={isPositive}>
					{formatChange(change)}
				</ChangeValue>
				<ChangePercent $isPositive={isPositive}>
					{formatPercent(rate)}
				</ChangePercent>
			</ChangeCell>
		</Row>
	)
}

export default React.memo(MarketRow)
